import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@parentpath/i18n';
import { supabase } from '@/lib/supabase';

// Types for premium content values
interface PremiumFeatureValue {
  name: string;
  free: boolean | string;
  premium: boolean | string;
}

interface PremiumSlideshowValue {
  image_url: string;
  alt_text: string;
}

export interface PremiumFeature {
  id: string;
  name: string;
  free: boolean | string;
  premium: boolean | string;
  sortOrder: number;
}

export interface PremiumSlide {
  id: string;
  imageUrl: string;
  altText: string;
  sortOrder: number;
}

export interface PremiumContentData {
  features: PremiumFeature[];
  slides: PremiumSlide[];
  isLoading: boolean;
  error: Error | null;
}

export function usePremiumContent() {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'de';

  const { data, isLoading, error } = useQuery({
    queryKey: ['premium-content', locale],
    queryFn: async () => {
      // Try current locale first, fall back to 'de' if no results
      let { data: content, error } = await supabase
        .from('premium_content')
        .select('*')
        .eq('is_active', true)
        .eq('locale', locale)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching premium content:', error);
        throw error;
      }

      // Fallback to German if no content for current locale
      if ((!content || content.length === 0) && locale !== 'de') {
        const fallback = await supabase
          .from('premium_content')
          .select('*')
          .eq('is_active', true)
          .eq('locale', 'de')
          .order('sort_order', { ascending: true });

        if (fallback.error) throw fallback.error;
        content = fallback.data;
      }

      // Parse features
      const features: PremiumFeature[] = (content || [])
        .filter(item => item.content_type === 'feature')
        .map(item => {
          const value = item.content_value as unknown as PremiumFeatureValue;
          return {
            id: item.id,
            name: value.name,
            free: value.free,
            premium: value.premium,
            sortOrder: item.sort_order,
          };
        });

      // Parse slideshow images
      const slides: PremiumSlide[] = (content || [])
        .filter(item => item.content_type === 'slideshow_image')
        .map(item => {
          const value = item.content_value as unknown as PremiumSlideshowValue;
          return {
            id: item.id,
            imageUrl: value.image_url,
            altText: value.alt_text,
            sortOrder: item.sort_order,
          };
        });

      return { features, slides };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fallback features if none in DB
  const defaultFeatures: PremiumFeature[] = [
    { id: '1', name: 'SSW-Tracker', free: true, premium: true, sortOrder: 1 },
    { id: '2', name: 'Baby-Entwicklung', free: true, premium: true, sortOrder: 2 },
    { id: '3', name: 'Basis-Checklisten', free: true, premium: true, sortOrder: 3 },
    { id: '4', name: 'KI-Assistent', free: false, premium: 'Unbegrenzt', sortOrder: 4 },
    { id: '5', name: 'Namenswahl', free: false, premium: true, sortOrder: 5 },
    { id: '6', name: 'Partner einladen', free: false, premium: true, sortOrder: 6 },
    { id: '7', name: 'Alle Checklisten', free: false, premium: true, sortOrder: 7 },
    { id: '8', name: 'Schlafplan', free: false, premium: true, sortOrder: 8 },
    { id: '9', name: 'Werbefrei', free: false, premium: true, sortOrder: 9 },
  ];

  return {
    features: data?.features?.length ? data.features : defaultFeatures,
    slides: data?.slides || [],
    isLoading,
    error: error as Error | null,
  };
}
